import { AuthContext } from 'context/AuthContext';
import { useContext, useEffect, useRef } from 'react';
import { HeaderFilterService } from 'services/main';
import { useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { parseJwt } from 'utils/parse';
import * as Sentry from '@sentry/react';

const isValidObject = obj => {
  if (!obj) return false;

  const validate = value => {
    if (value === null || value === undefined || value === '') return false;

    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.values(value).every(validate);
    }

    return true;
  };

  return validate(obj);
};

const Amplifique = () => {
  const { authContext } = useContext(AuthContext);
  const { user } = authContext;

  const [cookie] = useCookies();
  const location = useLocation();
  const pathname = location.pathname;

  const initialized = useRef(false);

  useEffect(() => {
    if (
      !user?.id ||
      !cookie?.authToken ||
      !cookie?.companies?.id ||
      pathname === '/login' ||
      pathname === '/logout' ||
      pathname.startsWith('/chamado')
    ) {
      return;
    }

    if (initialized.current) return;

    const email = `${user.id}@userleankeep.com`;

    const styleTag = document.createElement('style');

    const loadScript = () => {
      return new Promise((resolve, reject) => {
        if (document.querySelector('script[src="https://cdn.amplifique.me/amplifiqueme-inapp-survey.js"]')) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.amplifique.me/amplifiqueme-inapp-survey.js';
        script.defer = true;

        script.onload = resolve;
        script.onerror = reject;

        document.body.appendChild(script);
      });
    };

    const initSurvey = async () => {
      if (!user || !cookie?.companies?.id) return;
      try {
        if (!document.body) return;

        const companyResponse = await HeaderFilterService.company(cookie.companies.id, cookie.authToken);

        const companyType = companyResponse?.data?.tipoNome;

        let tokenPayload;

        try {
          tokenPayload = parseJwt(cookie.authToken);
        } catch (err) {
          console.error('Token parse error', err);
          Sentry.captureException(err);
          return;
        }

        const userTypeMap = {
          1: 'Super-Usuário',
          2: 'Administrador',
          3: 'Operacional',
          4: 'Chamado',
        };

        const userType = userTypeMap[Number(tokenPayload?.utyp)] || 'Desconhecido';

        const data = {
          email,
          created_at: user?.signUpUnixDate,
          name: user?.fullName,
          company: cookie?.companies?.name,
          custom_fields: {
            nome_empresa: cookie?.companies?.name,
            nome_usuario: user?.fullName,
            tipo_usuario: userType,
            segmento: companyType,
            idUsuario: user?.id,
            ehAdmin: user?.type === 'Operacional' ? 'True' : 'False',
            idEmpresa: String(cookie?.companies?.id),
            plataforma: 'Web-Novo',
            nomeEmpresa: cookie?.companies?.name,
            os: 'WEB',
          },
        };

        if (!isValidObject(data)) {
          // Sentry.captureException(new Error('Amplifique survey data is not valid'), {
          //   extra: { data },
          //   tags: {
          //     feature: 'amplifique-survey',
          //     Empresa: cookie?.companies?.name,
          //   },
          // });

          // console.error('Amplifique survey data is not valid:', data);
          return;
        }

        await loadScript();

        const runIdentify = () => {
          window.Amplifiqueme.identify(data, true);
          window.Amplifiqueme.load?.();
          window.Amplifiqueme.render?.();
          styleTag.innerHTML = `
      .amp-icon-cancel:before {
        display: block !important;
        z-index: 99999 !important;
      }
    `;
          document.head.appendChild(styleTag);
          initialized.current = true;
        };

        if (window.Amplifiqueme?.identify) {
          runIdentify();
        } else {
          window.Amplifiqueme = window.Amplifiqueme || {};
          window.Amplifiqueme.onLoad = runIdentify;
        }

        const existingSurveyElement = document.querySelector('ampl-survey[identifier="64286cc685cacdbcf60441ac"]');

        if (!existingSurveyElement) {
          const amplSurveyElement = document.createElement('ampl-survey');

          amplSurveyElement.setAttribute('identifier', '64286cc685cacdbcf60441ac');

          //Teste
          if (process.env.REACT_APP_MODE === 'homolog') {
            amplSurveyElement.setAttribute('force', 'true');
            amplSurveyElement.setAttribute('debug', 'true');
          }
          document.body.appendChild(amplSurveyElement);
        }
      } catch (error) {
        console.error('Error initializing Amplifique survey:', error);

        Sentry.captureException(error, {
          tags: {
            feature: 'amplifique-survey',
            Empresa: cookie?.companies?.name,
          },
        });
      }
    };

    initSurvey();

    return () => {
      const existingSurveyElement = document.querySelector('ampl-survey[identifier="64286cc685cacdbcf60441ac"]');

      if (existingSurveyElement?.parentNode) {
        existingSurveyElement.parentNode.removeChild(existingSurveyElement);
      }

      if (styleTag?.parentNode) {
        styleTag.parentNode.removeChild(styleTag);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, cookie, pathname]);

  return null;
};

export default Amplifique;
